import React from "react";

import {
  faExternalLink,
  faTriangleExclamation,
} from "@fortawesome/pro-regular-svg-icons";

import { Flow, FlowVersion } from "src/api/flowTypes";
import { useArchiveFlowVersion } from "src/api/flowVersionQueries";
import { ConfirmationModal } from "src/base-components/ConfirmationModal";
import { DependencyList } from "src/base-components/DependencyList";
import { ExternalLink } from "src/base-components/ExternalLink";
import { TextConfirmationModal } from "src/base-components/TextConfirmationModal";
import { EmptyState } from "src/design-system/EmptyState";
import { Icon } from "src/design-system/Icon";
import { Modal } from "src/design-system/Modal";
import { TAKTILE_TEAM_NOTIFIED } from "src/design-system/Toast/constants";
import { toastActions } from "src/design-system/Toast/utils";
import {
  agentVersionsToDependencyItems,
  DependencyModalSkeleton,
  flowParentsToDependencyItems,
} from "src/flow/modals/utils";
import { DraftPill, PublishedPill } from "src/flow/Pills";
import {
  useDraftAgentsDependingOnFlowVersion,
  usePublishedAgentsDependingOnFlowVersion,
} from "src/flow/useAgentDependencies";
import { useDeferredLoading } from "src/hooks/useDeferredLoading";
import { useJobs } from "src/jobs/api/queries";
import { useFlowContext } from "src/router/routerContextHooks";
import { getBaseUrl, getUrlToJobPage } from "src/router/urls";
import { useUserData } from "src/store/AuthStore";
import { logger } from "src/utils/logger";

type ArchiveVersionModalProps = {
  open: boolean;
  onClose: () => void;
  flow: Flow;
  flowVersion: FlowVersion | undefined;
  afterLeave?: () => void;
  onOpenManageTrafficModal: () => void;
  flowSlug: string;
};

type ModalState =
  | "has_traffic"
  | "has_default_version"
  | "has_active_jobs"
  | "loading_deps"
  | "error"
  | "has_published_deps"
  | "archive_version"
  | false;

export const ArchiveVersionModal: React.FC<ArchiveVersionModalProps> = ({
  open,
  onClose,
  flow,
  flowVersion,
  afterLeave,
  onOpenManageTrafficModal,
  flowSlug,
}) => {
  const { orgId, workspace } = useFlowContext();
  const { mutateAsync: archiveVersion } = useArchiveFlowVersion();
  const { signed_in_user_id } = useUserData();
  const { data: jobs } = useJobs({
    baseUrl: workspace.base_url!,
    flowId: flow.id,
  });

  // Sync blocking conditions (checked before agent deps)
  const trafficAllocation =
    flow.active_traffic_policy?.policy.flow_versions?.find(
      (version) => version.flow_version_id === flowVersion?.id,
    );
  const hasSandboxDefaultVersion =
    flow.default_sandbox_version === flowVersion?.id;
  const hasAnyDefaultVersion =
    hasSandboxDefaultVersion ||
    (!flow.active_traffic_policy && flow.default_version === flowVersion?.id);
  const activeJobs = jobs?.filter(
    (job) => job.flow_version_id === flowVersion?.id && job.status === "active",
  );
  const hasActiveJobs = !!activeJobs?.length;

  const hasSyncBlocker =
    !!trafficAllocation || hasAnyDefaultVersion || hasActiveJobs;

  // Agent deps — lazy fetch, only when modal is open and no sync blockers
  const {
    data: publishedAgentVersions,
    isError: isPublishedError,
    refetch: refetchPublished,
  } = usePublishedAgentsDependingOnFlowVersion(flowSlug, flowVersion?.id, {
    enabled: open && !hasSyncBlocker,
  });
  const {
    data: draftAgentVersions,
    isError: isDraftError,
    refetch: refetchDraft,
  } = useDraftAgentsDependingOnFlowVersion(flowSlug, flowVersion?.id, {
    enabled: open && !hasSyncBlocker,
  });

  const hasAgentDepsError = isPublishedError || isDraftError;
  const retryAgentDeps = () => {
    if (isPublishedError) refetchPublished();
    if (isDraftError) refetchDraft();
  };

  const agentDepsResolved =
    publishedAgentVersions !== undefined && draftAgentVersions !== undefined;
  const { showIndicator, suppress } = useDeferredLoading(
    !agentDepsResolved,
    open && !hasSyncBlocker,
  );

  // Published flow parent items
  const publishedFlowItems = flowParentsToDependencyItems({
    flowVersion,
    orgId,
    wsId: workspace.id,
    statusFilter: "published",
  });

  // Published agent items
  const publishedAgentItems = agentVersionsToDependencyItems(
    publishedAgentVersions ?? [],
    orgId,
    workspace.id,
    <PublishedPill />,
  );

  // Draft agent items (FYI)
  const draftAgentItems = agentVersionsToDependencyItems(
    draftAgentVersions ?? [],
    orgId,
    workspace.id,
    <DraftPill />,
  );

  // All non-archived flow parent items (for FYI in confirmation)
  const allFlowItems = flowParentsToDependencyItems({
    flowVersion,
    orgId,
    wsId: workspace.id,
    filterArchivedVersions: true,
  });

  const allPublishedItems = [...publishedFlowItems, ...publishedAgentItems];
  const hasPublishedDeps = allPublishedItems.length > 0;

  const fYIItems = [...allFlowItems, ...draftAgentItems];

  const determineModalState = (): ModalState => {
    if (!open) return false;
    // Sync blockers must be checked before suppress — they don't depend on
    // async agent deps and should surface immediately.
    if (trafficAllocation) return "has_traffic";
    if (hasAnyDefaultVersion) return "has_default_version";
    if (hasActiveJobs) return "has_active_jobs";
    // Separate from !open: suppress hides the brief loading flash while
    // agent deps are being fetched (controlled by useDeferredLoading).
    if (suppress) return false;
    if (hasAgentDepsError) return "error";
    if (showIndicator) return "loading_deps";
    if (hasPublishedDeps) return "has_published_deps";
    if (!agentDepsResolved) return "loading_deps";
    return "archive_version";
  };

  const modal = determineModalState();

  // Only fire afterLeave when the parent is closing the modal (open=false).
  // During internal state transitions (loading→confirmation), sub-modals close
  // without triggering afterLeave, which would prematurely clear the version prop.
  const resolvedAfterLeave = open ? undefined : afterLeave;

  const archiveFlowVersionHandler = async () => {
    if (!flowVersion) return;
    try {
      await archiveVersion({
        version: flowVersion,
        archived_by_id: signed_in_user_id ?? "",
        archived_at: new Date(Date.now()).toISOString(),
      });
      toastActions.success({
        title: "Version archived successfully",
        description: `Version ${flowVersion.name} of ${flow.name} was archived successfully.`,
      });
      onClose();
    } catch (error) {
      toastActions.failure({
        title: "Flow version archiving failed",
        description: TAKTILE_TEAM_NOTIFIED,
      });
      logger.error(`Error when archiving flow: ${error}`);
    }
  };

  return (
    <>
      {/* Sync blocker: active traffic */}
      <ConfirmationModal
        afterLeave={resolvedAfterLeave}
        confirmationButtonText="Change routing policy"
        open={modal === "has_traffic"}
        title={`Version ${flowVersion?.name} has active traffic`}
        onClose={onClose}
        onConfirm={onOpenManageTrafficModal}
      >
        <p className="mb-2 font-inter-normal-12px text-gray-500">
          Version {flowVersion?.name} has{" "}
          <strong className="font-inter-medium-12px text-gray-800">
            {trafficAllocation?.percentage}%
          </strong>{" "}
          active traffic allocation.
        </p>
        <p className="font-inter-normal-12px text-gray-500">
          You cannot archive a version with active traffic. Please edit the
          routing policy to remove traffic from this version before archiving.
        </p>
      </ConfirmationModal>

      {/* Sync blocker: default version */}
      <Modal
        afterLeave={resolvedAfterLeave}
        open={modal === "has_default_version"}
        size="sm"
        onClose={onClose}
      >
        <Modal.Header>
          {`Version ${flowVersion?.name} is set as default ${
            hasSandboxDefaultVersion ? "sandbox" : ""
          } version`}
        </Modal.Header>
        <Modal.Content>
          <p className="mb-2 font-inter-normal-12px text-gray-500">
            Version {flowVersion?.name} is set as default{" "}
            {hasSandboxDefaultVersion && "sandbox"} version.
          </p>
          <p className="font-inter-normal-12px text-gray-500">
            You cannot archive the {hasSandboxDefaultVersion && "sandbox"}{" "}
            default version. Please set another version as default before
            archiving this flow version.
          </p>
        </Modal.Content>
        <Modal.Footer primaryButton={false} secondaryButton="Close" />
      </Modal>

      {/* Sync blocker: active jobs */}
      <Modal
        afterLeave={resolvedAfterLeave}
        open={modal === "has_active_jobs"}
        onClose={onClose}
      >
        <Modal.Header>Cannot archive Version</Modal.Header>
        <Modal.Content>
          <p className="mb-2 font-inter-normal-12px text-gray-500">
            This version cannot be archived as it is being used by these active
            jobs
          </p>
          <ul className="mb-2.5 ml-4 list-disc font-inter-normal-13px">
            {activeJobs?.map((job) => (
              <li key={job.id}>
                <ExternalLink
                  className="flex items-center gap-x-1 text-gray-800"
                  href={
                    getBaseUrl() +
                    getUrlToJobPage(orgId, workspace.id, job.flow_id, job.id)
                  }
                >
                  {job.name}
                  <Icon icon={faExternalLink} size="2xs" />
                </ExternalLink>
              </li>
            ))}
          </ul>
          <p className="font-inter-normal-12px text-gray-500">
            Please deactivate these jobs before archiving this version.
          </p>
        </Modal.Content>
        <Modal.Footer primaryButton={false} secondaryButton="Close" />
      </Modal>

      {/* Loading skeleton while fetching agent deps */}
      <Modal
        afterLeave={resolvedAfterLeave}
        open={modal === "loading_deps"}
        size="sm"
        onClose={onClose}
      >
        <Modal.Header>Archive version</Modal.Header>
        <DependencyModalSkeleton />
      </Modal>

      {/* Error loading agent deps */}
      <Modal
        afterLeave={resolvedAfterLeave}
        open={modal === "error"}
        size="sm"
        onClose={onClose}
      >
        <Modal.Header>Archive version</Modal.Header>
        <Modal.Content>
          <EmptyState
            actionButton={{ label: "Retry", onClick: retryAgentDeps }}
            description="Please try again."
            headline="Failed to load dependencies"
            icon={faTriangleExclamation}
            size="compact"
            variant="error"
          />
        </Modal.Content>
        <Modal.Footer primaryButton={false} />
      </Modal>

      {/* Blocking: published dependencies */}
      <Modal
        afterLeave={resolvedAfterLeave}
        open={modal === "has_published_deps"}
        onClose={onClose}
      >
        <Modal.Header description="This version cannot be archived because it is used by published versions of other resources:">
          {`Cannot archive version "${flowVersion?.name}"`}
        </Modal.Header>
        <Modal.Content>
          <DependencyList
            dataLoc="archive-published-dependencies"
            items={allPublishedItems}
          />
          <p className="mt-2.5 font-inter-normal-13px text-gray-500">
            Please remove these connections before archiving this version.
          </p>
        </Modal.Content>
        <Modal.Footer primaryButton={false} />
      </Modal>

      {/* Confirmation with draft FYI — only mount once agent deps resolved to prevent flash */}
      {agentDepsResolved && (
        <TextConfirmationModal
          additionalContent={
            fYIItems.length > 0 ? (
              <div className="mb-4">
                <DependencyList
                  dataLoc="archive-draft-dependencies"
                  items={fYIItems}
                />
              </div>
            ) : undefined
          }
          afterLeave={resolvedAfterLeave}
          challengeText={flowVersion?.name}
          confirmButtonText="Archive version"
          description={
            fYIItems.length > 0
              ? "Are you sure you want to archive this Decision Flow version? All API calls to this version will fail and the flow will be locked to prevent further edits. You can un-archive this version later. Archiving this version may break the connections listed below."
              : "Are you sure you want to archive this Decision Flow version? All API calls to this version will fail and the flow will be locked to prevent further edits. You can un-archive this version later."
          }
          label="Please enter the version name to confirm"
          open={modal === "archive_version"}
          title={`Archive "${flowVersion?.name}" version`}
          onClose={onClose}
          onConfirm={archiveFlowVersionHandler}
        />
      )}
    </>
  );
};
